import { isEmpty, isEqual } from 'lodash';
import { ALL_VISITORS } from '@contentsquare/global-constants';
import { ComparisonOperators, invertComparisonOperator } from './comparison-operators';
import { FOV_ALL_VISITORS, FOV_EMPTY, FovFieldTypes, FovGroupTypes, FovNodeTypes, } from './fov-models';
import { FovError } from './fov-errors';
// FIXME can probably go away in the final version
export class FovNodes {
    static get(type) {
        switch (type) {
            case FovNodeTypes.AND:
                return this.andGroup();
            case FovNodeTypes.OR:
                return this.orGroup();
            case FovNodeTypes.SEQ:
                return this.sequenceGroup();
            case FovNodeTypes.CSEQ:
                return this.continuesSequenceGroup();
            case FovNodeTypes.FILTER:
                return this.filter(FovFieldTypes.GoalId);
            default:
                return this.andGroup();
        }
    }
    static continuesSequenceGroup(...children) {
        return {
            children,
            nodeType: FovNodeTypes.CSEQ,
        };
    }
    static sequenceGroup(...children) {
        return {
            children,
            nodeType: FovNodeTypes.SEQ,
        };
    }
    static orGroup(...children) {
        return {
            children,
            nodeType: FovNodeTypes.OR,
        };
    }
    static andGroup(...children) {
        return {
            children,
            nodeType: FovNodeTypes.AND,
        };
    }
    static blankGroup(...children) {
        return {
            children,
            nodeType: FovNodeTypes.AND,
        };
    }
    static not(child) {
        return {
            child,
            nodeType: FovNodeTypes.NOT,
        };
    }
    // equivalent to a not operator for a Page
    // the nodeType NOT is not supported inside a page fov
    // that's why we have to invert all operators
    static notPage(fovNode) {
        switch (fovNode.nodeType) {
            case FovNodeTypes.AND:
            case FovNodeTypes.OR:
                return Object.assign(Object.assign({}, fovNode), { nodeType: fovNode.nodeType === FovNodeTypes.OR ? FovNodeTypes.AND : FovNodeTypes.OR, children: fovNode.children.map((child) => this.notPage(child)) });
            case FovNodeTypes.FILTER: {
                const operator = invertComparisonOperator(fovNode.operator);
                if (!operator)
                    throw new Error('Unknown Operator');
                return Object.assign(Object.assign({}, fovNode), { operator });
            }
            default:
                throw new Error('nodeType not supported in a page fov');
        }
    }
    static filter(field, operator = ComparisonOperators.MATCH_EQUALS, value = null) {
        return {
            field,
            operator,
            value,
            nodeType: FovNodeTypes.FILTER,
        };
    }
    static isGroup(node) {
        if (!node) {
            return false;
        }
        return Object.values(FovGroupTypes).includes(node.nodeType);
    }
    static isNotGroup(node) {
        if (!node) {
            return false;
        }
        return node.nodeType === FovNodeTypes.NOT;
    }
    static isFilter(node) {
        if (!node) {
            return false;
        }
        return node.nodeType === FovNodeTypes.FILTER;
    }
    /** @deprecated Use isEmptyFovV2 instead */
    static isEmptyFov(node) {
        return !node || isEqual(node, FOV_EMPTY) || isEqual(node, FOV_ALL_VISITORS);
    }
    static isEmptyFovV2(target) {
        var _a;
        if (!target || !target.children) {
            return true;
        }
        const fovFilters = (_a = target.children[0]) === null || _a === void 0 ? void 0 : _a.children;
        if (!fovFilters || fovFilters.length < 1 || (fovFilters.length === 1 && isEmpty(fovFilters[0]))) {
            return true;
        }
        return false;
    }
    static isSamePageViewValue(node) {
        if (!node) {
            return false;
        }
        return !!node.visitType;
    }
    static isPageReference(node) {
        if (!node) {
            return false;
        }
        return !isNaN(node.pageId);
    }
    static createFovFromUrl(url, options = {}) {
        const parsedUrl = new URL(url);
        const filters = [
            FovNodes.filter(FovFieldTypes.PageHostViewed, ComparisonOperators.MATCH_EQUALS, parsedUrl.host),
            FovNodes.filter(FovFieldTypes.PagePathViewed, ComparisonOperators.MATCH_EQUALS, parsedUrl.pathname),
        ];
        switch (options.matchQuery) {
            case 'strict':
                filters.push(FovNodes.filter(FovFieldTypes.PageQueryParamsViewed, ComparisonOperators.MATCH_EQUALS, parsedUrl.search.replace(/^\?/, '')));
                break;
            case 'keysAndValues':
                filters.push(...Array.from(parsedUrl.searchParams).map(([key, value]) => {
                    return FovNodes.filter(FovFieldTypes.PageQueryParamsViewed, ComparisonOperators.MATCH_CONTAINS, `${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
                }));
                break;
            case 'keysOnly':
                filters.push(...Array.from(parsedUrl.searchParams).map(([key]) => {
                    return FovNodes.filter(FovFieldTypes.PageQueryParamsViewed, ComparisonOperators.MATCH_CONTAINS, `${encodeURIComponent(key)}`);
                }));
                break;
        }
        return FovNodes.orGroup(FovNodes.andGroup(...filters));
    }
    static createFovFromSegmentIds(includedSegmentIds, excludedSegmentIds) {
        const includedSegments = [];
        const excludedSegments = [];
        if (includedSegmentIds && includedSegmentIds.length) {
            for (const includedSegmentId of includedSegmentIds) {
                if (includedSegmentId === ALL_VISITORS) {
                    continue;
                }
                includedSegments.push({
                    nodeType: FovNodeTypes.FILTER,
                    field: FovFieldTypes.SegmentId,
                    operator: ComparisonOperators.MATCH_EQUALS,
                    value: includedSegmentId,
                });
            }
        }
        if (excludedSegmentIds && excludedSegmentIds.length) {
            for (const excludedSegmentId of excludedSegmentIds) {
                if (excludedSegmentId === ALL_VISITORS) {
                    throw new FovError(`FOV.BUILDER`, `Cannot exclude segments all visitors.`);
                }
                excludedSegments.push({
                    nodeType: FovNodeTypes.FILTER,
                    field: FovFieldTypes.SegmentId,
                    operator: ComparisonOperators.MATCH_NOT_EQUALS,
                    value: excludedSegmentId,
                });
            }
        }
        return FovNodes.andGroup(...includedSegments, ...excludedSegments);
    }
}
//# sourceMappingURL=fov-nodes.js.map